import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';
import { message, Alert } from '@brokerbay/design-components';
import {
  getKeyholderBillingProfile,
  getJpmTransactionInfoKeyholder,
} from 'api/SupraController';
import type {
  IJpmcTransaction,
  ISubscribeAnsweringServiceBillingProfile,
} from '@/app-package';
import { Color, Text } from '@bb/elements-web';
import { Modal, Spin } from 'components';
import { BillingInvoicesPageContext } from '../../pages/BillingInvoicesPage/BillingInvoicesPageContext';
import { ManageCardContext } from './ManageCardContext';

/**
 * Renders the Chase (JPMC) iframe in a modal.
 * – Shows a loader while the session‑id is being fetched by the context.

 *
 * 1. Loads hpfParent.min.js in the parent page *before* the iframe initialises.
 * 2. Exposes `handlePaymentErrors`, `completePayment`, and `cancelPayment` on
 *    `window` so the iframe can call them even though it is cross‑origin.
 * 3. Surfaces validation errors inside the modal
 * 4. Handles postMessage events from iframe (e.g., "what's this?" clicks)
 */

const hpfParentJS = `${process.env.JPMC_IFRAME_BASE_URL}js/hpfParent.min.js`;

declare global {
  interface Window {
    handlePaymentErrors?: (content: {
      messageId?: string;
      errorCode?: string[];
      subject?: string;
      reply?: boolean;
    }) => void;
    completePayment?: (txn: unknown) => void;
    cancelPayment?: (content: string) => void;
  }
}

interface UpdateJPMCModalProps {
  setBillingInfo?: Dispatch<
    SetStateAction<
      Partial<ISubscribeAnsweringServiceBillingProfile> | undefined
    >
  >;
  showFooter?: boolean;
}

const UpdateJPMCModal: React.FC<UpdateJPMCModalProps> = ({
  setBillingInfo,
  showFooter = false,
}) => {
  const {
    modalVisible,
    setModalVisible,
    refetchCardInfo,
    onCardUpdateSuccess,
    jpmcSessionId,
    setJPMCSessionId,
    setLoadJPMCSession,
    orgId,
    billingCustomerNumber,
    jpmErrorMap,
  } = useContext(ManageCardContext);

  const {
    updateBillingProfile,
    updateProfileError,
    billingProfile,
    isUpdatingProfile,
  } = useContext(BillingInvoicesPageContext);

  const jpmcIframeUrl = `${process.env.JPMC_IFRAME_BASE_URL}1_1/?`;
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [helpContent, setHelpContent] = useState<string | null>(null);

  const closeAndResetIframe = useCallback(() => {
    setIframeError(null);
    setHelpContent(null);
    setLoadJPMCSession(true); // Ensure a new session will be loaded next time
    setJPMCSessionId(null);
    setModalVisible(false);
  }, [setModalVisible, setLoadJPMCSession, setJPMCSessionId]);

  useEffect(() => {
    if (modalVisible) {
      // Load a new session for the next render
      setLoadJPMCSession(true);
      setJPMCSessionId(null);
    }
  }, [modalVisible, setLoadJPMCSession, setJPMCSessionId]);

  // Load hpfParent.min.js and register the global callbacks *once per open*.
  useEffect(() => {
    if (!modalVisible || !jpmcSessionId) return;

    // Inject the helper script
    const scriptId = 'hpf-parent-js';
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script');
      s.id = scriptId;
      s.src = hpfParentJS;
      s.async = false; // important
      document.head.appendChild(s);
    }

    // Handle postMessage events from the iframe
    const handlePostMessage = (event: MessageEvent) => {
      // Verify origin for security - ensure it's from the JPMC iframe
      const jpmcBaseUrl = process.env.JPMC_IFRAME_BASE_URL;
      if (jpmcBaseUrl) {
        const jpmcHostname = new URL(jpmcBaseUrl).hostname;

        if (!event.origin.includes(jpmcHostname)) {
          return;
        }
      }

      // Handle structured postMessage objects
      if (event.data && typeof event.data === 'object') {
        const { subject, content } = event.data;

        if (subject === 'whatsThis') {
          const helpText =
            'CVC (Card Verification Code) is the three or four-digit security code on the back of your card.';

          if (content === 'cvv') {
            setHelpContent(helpText);
          }
        }
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Expose callback for HPF validation errors.
    window.handlePaymentErrors = ({ errorCode }) => {
      let friendly = 'Unable to update card.';

      if (errorCode && errorCode.length > 0) {
        const friendlyMessagesList = errorCode
          .map(code => jpmErrorMap[code]) // get the error strings
          .filter(Boolean); // get rid of nulls

        const uniqueFriendlyMessages = [...new Set(friendlyMessagesList)];

        if (uniqueFriendlyMessages.length > 0) {
          friendly = uniqueFriendlyMessages.join('\n');
        }
      }
      setIframeError(friendly);
    };

    // Expose callback for successful completion.
    window.completePayment = async () => {
      try {
        const jpmcInfo: IJpmcTransaction = await getJpmTransactionInfoKeyholder(
          { JPMCUid: jpmcSessionId },
        );
        if (!jpmcInfo) {
          message.error('Failed to fetch JPMC transaction info');
          return;
        }

        if (setBillingInfo) {
          setBillingInfo(prev => ({
            ...prev,
            jpmcSessionId,
            ccNumber: jpmcInfo.CCNumber,
            maskedCCNumber: jpmcInfo.MaskedCCNumber,
            ccExpiryDate: jpmcInfo.CCExpiryDate,
            cardholderName: jpmcInfo.CardholderName,
            ccType: jpmcInfo.CardType,
          }));
        } else {
          let profile = billingProfile;
          if (!billingProfile) {
            profile = await getKeyholderBillingProfile({
              orgId,
              billingCustomerNumber,
            });
          }

          if (!profile) {
            message.error('Failed to fetch billing profile');
            return;
          }

          await updateBillingProfile(
            {
              ...profile,
              JPUid: jpmcSessionId,
              CCNumber: jpmcInfo.CCNumber,
              MaskedCCNumber: jpmcInfo.MaskedCCNumber,
              CCExpiryDate: jpmcInfo.CCExpiryDate,
              CardholderName: jpmcInfo.CardholderName,
              CCType: jpmcInfo.CardType,
            },
            true,
          );
          if (!updateProfileError) {
            message.success('Card updated.');
          }
        }
      } catch (e) {
        message.error('Failed to update card');
      } finally {
        refetchCardInfo?.();
        onCardUpdateSuccess?.();
        closeAndResetIframe();
      }
    };

    window.cancelPayment = () => {
      closeAndResetIframe();
    };

    return () => {
      window.removeEventListener('message', handlePostMessage);
      delete window.handlePaymentErrors;
      delete window.completePayment;
      delete window.cancelPayment;
    };
  }, [
    modalVisible,
    jpmcSessionId,
    refetchCardInfo,
    onCardUpdateSuccess,
    jpmErrorMap,
    orgId,
    billingCustomerNumber,
    closeAndResetIframe,
    billingProfile,
    updateBillingProfile,
    updateProfileError,
    setBillingInfo,
    setModalVisible,
  ]);

  return (
    <Modal
      title="Update Card"
      open={modalVisible}
      onCancel={() => closeAndResetIframe()}
      footer={null}
      width={420}
      destroyOnClose
    >
      <Spin
        spinning={!jpmcSessionId || isUpdatingProfile}
        center
        size="large"
      />

      {iframeError && !isUpdatingProfile && (
        <Alert
          type="error"
          message={iframeError}
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
        />
      )}

      {helpContent && !isUpdatingProfile && (
        <Alert
          type="info"
          message="Help Information"
          description={helpContent}
          closable
          onClose={() => setHelpContent(null)}
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
        />
      )}

      {jpmcSessionId && !isUpdatingProfile && (
        <IframeWrapper>
          <iframe
            key={jpmcSessionId ?? 0}
            id="jpmc-frame"
            src={`${jpmcIframeUrl}uID=${jpmcSessionId}`}
            width="100%"
            height="175"
            title="Chase payment iframe"
          />
          {showFooter && (
            <Text>
              By updating your payment information, you authorize Supra to
              charge your balances on your billing days per subscription and
              once every plan cycle until cancelled. The card saved will be the
              default payment method used for every charge. Your recurring
              payment is not refundable. Missed payments are subject to a fee.{' '}
              <StyledTOCLink
                href="https://www.brokerbay.com/supraone-user-terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </StyledTOCLink>
            </Text>
          )}
        </IframeWrapper>
      )}
    </Modal>
  );
};

const StyledTOCLink = styled.a`
  font-family: inherit;
  font-weight: bold;

  && {
    text-decoration: underline;
    color: ${Color.PRIMARY_TEXT};
  }
`;

const IframeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
`;

export default UpdateJPMCModal;
